import type { CSSProperties } from "react";
import { Search } from "lucide-react";
import type {
  IntegrationCategory,
  IntegrationFilter,
} from "../integrationTypes";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "../integrationMockData";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

/**
 * The search + category-chip toolbar for the Integration transactions
 * page.
 *
 * Search is a pill-shaped input, 280px fixed on desktop (full-width on
 * mobile), white background by default, border darkens on hover, dark
 * ink focus ring on click. Not full-width so the chip row sits next to
 * it on desktop.
 *
 * Stateless: receives filter + onFilterChange from parent.
 */

interface IntegrationToolbarProps {
  filter: IntegrationFilter;
  onFilterChange: (filter: IntegrationFilter) => void;
}

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
};

interface CategoryChip {
  value: IntegrationCategory | "all";
  label: string;
}

const CATEGORY_CHIPS: CategoryChip[] = [
  { value: "all", label: "All" },
  ...CATEGORY_ORDER.map((cat) => ({
    value: cat,
    label: CATEGORY_LABELS[cat],
  })),
];

export function IntegrationToolbar({
  filter,
  onFilterChange,
}: IntegrationToolbarProps) {
  const { isMobile } = useBreakpoint();

  const wrapStyle: CSSProperties = {
    display: "flex",
    gap: 10,
    alignItems: isMobile ? "stretch" : "center",
    flexDirection: isMobile ? "column" : "row",
    flexWrap: "wrap",
    padding: isMobile ? "12px 16px" : "14px 32px",
    borderBottom: `1px solid ${PALETTE.lineSoft}`,
    background: "#ffffff",
  };

  const searchWrapStyle: CSSProperties = {
    position: "relative",
    width: isMobile ? "100%" : 280,
    flexShrink: 0,
  };

  return (
    <div style={wrapStyle}>
      <div style={searchWrapStyle}>
        <Search
          size={15}
          color={PALETTE.ink4}
          strokeWidth={2}
          style={searchIconStyle}
        />
        <input
          type="search"
          placeholder="Search integrations"
          value={filter.search}
          onChange={(e) =>
            onFilterChange({ ...filter, search: e.target.value })
          }
          style={searchInputStyle}
          aria-label="Search integrations"
        />
      </div>

      <div style={chipRowStyle} role="group" aria-label="Category filter">
        {CATEGORY_CHIPS.map((chip) => {
          const active = filter.category === chip.value;
          return (
            <button
              key={chip.value}
              type="button"
              aria-pressed={active}
              style={{
                ...chipStyle,
                background: active ? PALETTE.ink : "transparent",
                color: active ? "#ffffff" : PALETTE.ink2,
                borderColor: active ? PALETTE.ink : PALETTE.line,
              }}
              onClick={() =>
                onFilterChange({ ...filter, category: chip.value })
              }
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const searchIconStyle: CSSProperties = {
  position: "absolute",
  top: "50%",
  left: 14,
  transform: "translateY(-50%)",
  pointerEvents: "none",
};

const searchInputStyle: CSSProperties = {
  width: "100%",
  padding: "9px 16px 9px 38px",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 500,
  background: "#ffffff",
  outline: "none",
  fontFamily: "inherit",
  color: PALETTE.ink,
  transition: "border-color 120ms, box-shadow 120ms",
};

const chipRowStyle: CSSProperties = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
};

const chipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 11px",
  borderRadius: 20,
  border: "1px solid",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  whiteSpace: "nowrap",
  fontFamily: "inherit",
  transition: "all 120ms ease",
};